import { useState, useEffect } from 'react'
import Modal from '../shared/Modal'
import AircraftOptions from '../aircraft/AircraftOptions'
import { PriceInput } from '../shared/PriceInput'
import { PriceInputWithModifier } from '../shared/PriceInputWithModifier'
import { Aircraft } from '../../types/intellijetnet'
import { MarketCategory, AircraftOverrideInput, MarketEMP } from '../../types/database'
import { OffMarketService } from '../../services/off-market.service'
import { OverridesService } from '../../services/overrides.service'
import { MarketEMPsService } from '../../services/market-emps.service'
import { aircraftToOverrideInput } from '../../utils/overrides'
import { AIRCRAFT_STATUS_OPTIONS } from '../../constants/aircraft-status'
import { useOverrideRefresh } from '../../contexts/OverrideRefreshContext'
import { invalidateAircraftOptionsCache } from '../../hooks/useAircraftOptions'

interface AddOffMarketAircraftFormProps {
  isOpen: boolean
  aircraft: Aircraft
  modelId: number
  onSuccess: () => void
  onCancel: () => void
}

export default function AddOffMarketAircraftForm({
  isOpen,
  aircraft,
  modelId,
  onSuccess,
  onCancel
}: AddOffMarketAircraftFormProps) {
  const [marketCategory, setMarketCategory] = useState<MarketCategory>('for_sale')
  const [overrideData, setOverrideData] = useState<AircraftOverrideInput>({
    aircraft_id: aircraft.aircraft_id
  })
  const [availableEMPs, setAvailableEMPs] = useState<MarketEMP[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { refreshOverrideForAircraft } = useOverrideRefresh()

  // Load available EMPs when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableEMPs()
      // Initialize override data with aircraft data
      const initialOverride = aircraftToOverrideInput(aircraft) as AircraftOverrideInput
      setOverrideData(initialOverride)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, aircraft])

  const loadAvailableEMPs = async () => {
    try {
      const emps = await MarketEMPsService.getByModelId(modelId)
      setAvailableEMPs(emps)
    } catch (err: any) {
      // Error loading EMPs
    }
  }



  const handleSubmit = async () => {
    try {
      setSaving(true)
      setError(null)

      // Validate required data
      if (!aircraft || !aircraft.aircraft_id) {
        throw new Error('Invalid aircraft data. Please try selecting the aircraft again.')
      }

      if (!modelId) {
        throw new Error('Invalid model ID. Please refresh the page and try again.')
      }

      // First, save any override data if it differs from original aircraft data
      const hasOverrides = Object.keys(overrideData).some(key => {
        if (key === 'aircraft_id') return false
        const overrideValue = overrideData[key as keyof AircraftOverrideInput]
        const aircraftValue = aircraft[key as keyof Aircraft]
        return overrideValue !== null && overrideValue !== undefined && overrideValue !== aircraftValue
      })

      if (hasOverrides) {
        try {
          await OverridesService.upsert(overrideData)
          // Trigger override refresh for this aircraft
          refreshOverrideForAircraft(aircraft.aircraft_id)
        } catch (overrideErr: any) {
          throw new Error(`Failed to save override data: ${overrideErr.message || 'Unknown error'}`)
        }
      }

      // Create the off-market entry (this will also ensure aircraft record exists)
      try {
        await OffMarketService.create({
          aircraft_id: aircraft.aircraft_id,
          model_id: modelId,
          market_category: marketCategory
        })
      } catch (offMarketErr: any) {
        if (offMarketErr.message?.includes('already in off-market')) {
          throw new Error('This aircraft is already in the off-market list for this model.')
        }
        throw new Error(`Failed to add aircraft to market: ${offMarketErr.message || 'Unknown error'}`)
      }

      // Invalidate options cache to ensure the new aircraft shows up with options support
      invalidateAircraftOptionsCache([aircraft.aircraft_id])

      onSuccess()
    } catch (err: any) {
      setError(err.message || 'Failed to add aircraft to market')
    } finally {
      setSaving(false)
    }
  }

  const handleOverrideChange = (field: keyof AircraftOverrideInput, value: any) => {
    setOverrideData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const marketCategoryOptions = [
    { value: 'for_sale', label: 'For Sale' },
    { value: 'recently_sold', label: 'Recently Sold' },
    { value: 'removed_from_market', label: 'Removed From Market' }
  ] as const

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={`Add Aircraft to Market - ${aircraft.sernbr}`} size="xl">
      <div className="space-y-6 max-h-[80vh] overflow-y-auto">
        <div className="text-sm text-gray-600">
          Configure the aircraft details and select the market category before adding to the market summary.
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Market Category Selection */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Market Category</h3>
          <div className="space-y-2">
            {marketCategoryOptions.map((option) => (
              <label key={option.value} className="flex items-center">
                <input
                  type="radio"
                  name="marketCategory"
                  value={option.value}
                  checked={marketCategory === option.value}
                  onChange={(e) => setMarketCategory(e.target.value as MarketCategory)}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Optional Equipment */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Optional Equipment</h3>
          <AircraftOptions
            aircraftId={aircraft.aircraft_id}
            modelId={aircraft.model_id || modelId}
            readonly={false}
          />
        </div>

        {/* Override Data Section */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Override Data</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* AFTT */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AFTT (Hours)
              </label>
              <input
                type="number"
                value={overrideData.aftt || ''}
                onChange={(e) => handleOverrideChange('aftt', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={aircraft.aftt?.toString() || 'Not specified'}
              />
            </div>

            {/* TBO */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                TBO (Time Before Overhaul)
              </label>
              <input
                type="number"
                value={overrideData.tbo || ''}
                onChange={(e) => handleOverrideChange('tbo', e.target.value ? parseFloat(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder="Not specified"
              />
            </div>

            {/* Landings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Landings
              </label>
              <input
                type="number"
                value={overrideData.landings || ''}
                onChange={(e) => handleOverrideChange('landings', e.target.value ? parseInt(e.target.value) : null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={aircraft.landings?.toString() || 'Not specified'}
              />
            </div>

            {/* Base Airport */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Airport
              </label>
              <input
                type="text"
                value={overrideData.base_airport || ''}
                onChange={(e) => handleOverrideChange('base_airport', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={aircraft.baseairport || 'Not specified'}
              />
            </div>

            {/* Base City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base City
              </label>
              <input
                type="text"
                value={overrideData.base_city || ''}
                onChange={(e) => handleOverrideChange('base_city', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={aircraft.basecity || 'Not specified'}
              />
            </div>

            {/* Base State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base State
              </label>
              <input
                type="text"
                value={overrideData.base_state || ''}
                onChange={(e) => handleOverrideChange('base_state', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={aircraft.basestate || 'Not specified'}
              />
            </div>

            {/* Base Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Base Country
              </label>
              <input
                type="text"
                value={overrideData.base_country || ''}
                onChange={(e) => handleOverrideChange('base_country', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                placeholder={aircraft.basecountry || 'Not specified'}
              />
            </div>

            {/* Internal Asking Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="text-green-600 font-semibold">Internal</span> Ask Price
              </label>
              <PriceInput
                value={overrideData.asking_price}
                onChange={(value) => handleOverrideChange('asking_price', value)}
                placeholder="e.g., 1.234 for $1.234M"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* Internal Take Price */}
            <PriceInputWithModifier
              value={overrideData.take_price}
              modifier={overrideData.take_price_modifier}
              onValueChange={(value) => handleOverrideChange('take_price', value)}
              onModifierChange={(modifier) => handleOverrideChange('take_price_modifier', modifier)}
              placeholder="e.g., 1.234 for $1.234M"
              label={<><span className="text-green-600 font-semibold">Internal</span> Take Price</>}
            />

            {/* External Asking Price */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="text-red-600 font-semibold">External</span> Ask Price
              </label>
              <PriceInput
                value={overrideData.external_asking_price}
                onChange={(value) => handleOverrideChange('external_asking_price', value)}
                placeholder="e.g., 1.234 for $1.234M"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

            {/* External Take Price */}
            <PriceInputWithModifier
              value={overrideData.external_take_price}
              modifier={overrideData.external_take_price_modifier}
              onValueChange={(value) => handleOverrideChange('external_take_price', value)}
              onModifierChange={(modifier) => handleOverrideChange('external_take_price_modifier', modifier)}
              placeholder="e.g., 1.234 for $1.234M"
              label={<><span className="text-red-600 font-semibold">External</span> Take Price</>}
            />

            {/* EMP */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Engine Maintenance Program (EMP)
              </label>
              <select
                value={overrideData.emp || ''}
                onChange={(e) => handleOverrideChange('emp', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Select EMP...</option>
                {availableEMPs.map((emp) => (
                  <option key={emp.id} value={emp.name}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={overrideData.status || ''}
                onChange={(e) => handleOverrideChange('status', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Select status...</option>
                {AIRCRAFT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Damage History */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Damage History
              </label>
              <select
                value={overrideData.damage_history || ''}
                onChange={(e) => handleOverrideChange('damage_history', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
                <option value="Unknown">Unknown</option>
              </select>
            </div>

            {/* Date Listed */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Listed
              </label>
              <input
                type="date"
                value={overrideData.date_listed || ''}
                onChange={(e) => handleOverrideChange('date_listed', e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
              />
            </div>

          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-purple-950 border border-transparent rounded-md hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Adding to Market...' : 'Add to Market'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
